import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '30px 30px',
        }}></div>
      </div>

      {/* Floating Orbs */}
      <div className="absolute top-0 right-0 w-48 h-48 md:w-96 md:h-96 bg-teal-500/10 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 md:w-96 md:h-96 bg-emerald-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 lg:py-20 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 group">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <i className="ri-heart-3-fill text-2xl md:text-3xl text-white"></i>
              </div>
              <span className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                Loveish
              </span>
            </Link>
            <p className="text-emerald-100 mb-4 md:mb-6 leading-relaxed text-sm md:text-base">
              {t('footerDescription')}
            </p>
            <div className="flex items-center gap-3 md:gap-4">
              {[
                { icon: 'ri-facebook-fill', href: '#', color: 'hover:bg-blue-500' },
                { icon: 'ri-instagram-line', href: '#', color: 'hover:bg-pink-500' },
                { icon: 'ri-twitter-x-line', href: '#', color: 'hover:bg-gray-900' },
                { icon: 'ri-youtube-fill', href: '#', color: 'hover:bg-red-500' }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 md:w-12 md:h-12 bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl flex items-center justify-center ${social.color} transition-all duration-300 hover:scale-110 cursor-pointer`}
                  aria-label={`Social media link ${index + 1}`}
                >
                  <i className={`${social.icon} text-lg md:text-xl`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg md:text-xl font-bold mb-4 md:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t('quickLinks')}
            </h3>
            <ul className="space-y-2 md:space-y-3">
              {[
                { to: '/', label: t('home') },
                { to: '/products', label: t('products') },
                { to: '/about', label: t('about') },
                { to: '/contact', label: t('contact') }
              ].map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.to}
                    className="text-emerald-100 hover:text-white transition-colors duration-300 flex items-center gap-2 group cursor-pointer text-sm md:text-base"
                  >
                    <i className="ri-arrow-right-s-line text-lg md:text-xl group-hover:translate-x-1 transition-transform"></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="text-lg md:text-xl font-bold mb-4 md:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t('customerService')}
            </h3>
            <ul className="space-y-2 md:space-y-3">
              {[
                { to: '/faq', label: t('faq') },
                { to: '/privacy', label: t('privacyPolicy') },
                { to: '/contact', label: t('support') }
              ].map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.to}
                    className="text-emerald-100 hover:text-white transition-colors duration-300 flex items-center gap-2 group cursor-pointer text-sm md:text-base"
                  >
                    <i className="ri-arrow-right-s-line text-lg md:text-xl group-hover:translate-x-1 transition-transform"></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg md:text-xl font-bold mb-4 md:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t('newsletter')}
            </h3>
            <p className="text-emerald-100 mb-4 text-sm md:text-base">
              {t('newsletterDescription')}
            </p>
            <form className="space-y-3">
              <input
                type="email"
                placeholder={t('enterEmail')}
                className="w-full px-3 md:px-4 py-2 md:py-3 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl md:rounded-2xl text-white placeholder-emerald-200 focus:border-white focus:ring-4 focus:ring-white/20 transition-all duration-300 text-sm md:text-base"
              />
              <button
                type="submit"
                className="w-full px-4 md:px-6 py-2 md:py-3 bg-white text-emerald-600 rounded-xl md:rounded-2xl font-bold hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:scale-105 cursor-pointer whitespace-nowrap text-sm md:text-base"
              >
                {t('subscribe')}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 md:pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-emerald-100 text-xs md:text-sm">
            <p className="text-center md:text-left">
              © {currentYear} Loveish. {t('allRightsReserved')}
            </p>
            <div className="flex items-center gap-4 md:gap-6 flex-wrap justify-center">
              <Link to="/privacy" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                {t('privacyPolicy')}
              </Link>
              <span className="text-emerald-200/50">•</span>
              <a 
                href="https://www.linkedin.com/in/arman-khachatryan-b5a5b5200/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-white transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-code-s-slash-line text-base"></i>
                <span>Developed by Arman Khachatryan</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;